/**
 * Builds the user-facing message for a failed image-enhance response.
 *
 * Returns the message, never throws. A `responseHandler` that throws is caught by
 * fetchBaseQuery and collapsed into `{ status: "PARSING_ERROR", error: String(e) }`
 * — which discards the HTTP status and hands the caller a plain object, so an
 * `err instanceof Error` check at the call site is always false and every failure
 * renders as the generic fallback. Returning instead lets RTK's own `validateStatus`
 * build `{ error: { status, data: message } }`, keeping the status for telemetry.
 * Read it back with `getEnhanceErrorMessage`. (SN-18765)
 */
export async function parseEnhanceImageError(
  response: Response,
  fallbackMessage = "Image optimization failed"
): Promise<string> {
  if (response.status === 429) {
    return "You've reached the optimization limit. Try again in an hour.";
  }

  const contentType = response.headers.get("Content-Type") ?? "";
  if (contentType.includes("application/json")) {
    const body = await response.json().catch(() => null);
    // `custom_exception_handler` runs every APIException through `get_full_details()`,
    // so a field error is `{image: [{message, code}]}` and a throttle/permission error
    // is `{message, code}`. `detail` covers anything that skipped that handler.
    return (
      body?.image?.[0]?.message ??
      body?.message ??
      body?.detail ??
      fallbackMessage
    );
  }

  return `${fallbackMessage} (${response.status})`;
}

/**
 * Reads the message back off a rejected enhance mutation.
 *
 * `data` is where `parseEnhanceImageError`'s message lands. `error` covers the
 * failures that never reach the response handler at all — fetchBaseQuery returns
 * `FETCH_ERROR` (offline) and `TIMEOUT_ERROR` (the endpoints set a 120s timeout)
 * with the message stringified onto that key instead.
 */
export function getEnhanceErrorMessage(
  err: unknown,
  fallbackMessage: string
): string {
  if (typeof err === "object" && err !== null) {
    const { data, error } = err as { data?: unknown; error?: unknown };
    if (typeof data === "string" && data) {
      return data;
    }
    if (typeof error === "string" && error) {
      return error.replace(/^Error:\s*/, "");
    }
  }

  if (err instanceof Error && err.message) {
    return err.message;
  }

  return fallbackMessage;
}
